import { Icons, useHelmet } from '@agro-club/agroclub-shared'
import { useAnalyticsContext } from 'analytics/hooks'
import { format } from 'date-fns'
import { Tab, useTab, useTabsDataV2 } from 'hooks/useTabsDataV2'
import { YEAR_MONTH } from 'modules/constants'
import { AuthContext } from 'modules/context/AuthContext'
import { LogisticRequest } from 'modules/domain/logisticsKR/types'
import { useEventCount } from 'modules/socket/hooks'
import { getMonthList } from 'modules/utils/helpers'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router'
import { AAntdTabs, AButton } from 'views/components/Analytics'
import { Count } from 'views/components/Board/styled'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import * as Layout from 'views/layouts/NewLayout'
import * as Header from 'views/ui/Header/Header'
import { AnalyticsPages, AnalyticsPlaces } from '../../analyticsPlaces'
import { LogisticTabs, RequestType } from '../../types'
import DealsKRRoutes from '../routes'
import { ContentWrapper } from '../styled'
import { ClosedRequestBoardColumns } from './ClosedRequestBoardColumns/ClosedRequestBoardColumns'
import { OpenRequestsBoardColumns } from './OpenRequestsBoardColumns/OpenRequestsBoardColumns'
import { RequestsBoardFilters } from './RequestsBoardFilters/RequestsBoardFilters'
import { TripsWithoutTtnTable } from './TripsWithoutTtnTable/TripsWithoutTtnTable'
import { CarriersWithoutDocsTable } from './CarriersWithoutDocsTable/CarriersWithoutDocsTable'
import { CarrierFilters } from './RequestsBoardFilters/CarrierFilters'

const DEAL_LOGISTICIAN = 'deal_logistician'

const nonEditableParams = { pageSize: 10000 }
const currMonth = format(getMonthList()[0], YEAR_MONTH)

export const LogisticianBoard = () => {
  const { profile } = useContext(AuthContext)
  const eventsCount = useEventCount()
  const { t } = useTranslation('logisticBids')
  const { setOptions } = useAnalyticsContext()
  const { activeTab: activeTabParam, selectedRequest } = useParams()
  const [activeTab, setActiveTab] = useState(activeTabParam || LogisticTabs.open)
  const navigate = useNavigate()
  const initParamsWithoutTtn =
    profile?.profile?.coordinator_type === DEAL_LOGISTICIAN
      ? {
          initialParams: {
            filter: { assignee: profile?.id.toString() },
          },
        }
      : undefined
  const initParamsCarriersWithoutDocs =
    profile?.profile?.coordinator_type === DEAL_LOGISTICIAN
      ? {
          initialParams: {
            filter: { responsible_logistician: profile?.id.toString() },
          },
        }
      : undefined
  const tabs: Record<string, Tab> = {}
  tabs[LogisticTabs.open] = useTab({
    label: t('tabs.open'),
    nonEditableParams,
    initialParams: { filter: { source: RequestType.internal } },
  })
  tabs[LogisticTabs.closed] = useTab({
    label: t('tabs.closed'),
    initialParams: { filter: { month: currMonth, source: RequestType.internal } },
    nonEditableParams,
  })
  tabs[LogisticTabs.without_ttn] = useTab({
    label: t('tabs.without_ttn'),
    ...initParamsWithoutTtn,
  })
  tabs[LogisticTabs.carriersWithoutDocs] = useTab({
    label: t('tabs.carriersWithoutDocs'),
    ...initParamsCarriersWithoutDocs,
  })
  const { isFiltersLoaded } = useTabsDataV2({
    activeTab,
    tabs,
    independTabs: true,
  })
  const [activeRequest, setActiveRequest] = useState<LogisticRequest | undefined>(undefined)
  const [drawerIsOpen, setDrawerIsOpen] = useState(false)

  useHelmet({ title: t('logisticsKR:list:heading') })

  const openRequests = {
    label: t('tabs.open'),
    key: LogisticTabs.open,
    children: (
      <ContentWrapper>
        <OpenRequestsBoardColumns
          setActiveRequest={setActiveRequest}
          activeRequest={activeRequest}
          listRequestParams={tabs[LogisticTabs.open].params}
          drawerIsOpen={drawerIsOpen}
          setDrawerIsOpen={setDrawerIsOpen}
          isFiltersLoaded={isFiltersLoaded}
        />
      </ContentWrapper>
    ),
  }

  const closedRequests = {
    label: tabs[LogisticTabs.closed].label,
    key: LogisticTabs.closed,
    children: (
      <ContentWrapper>
        <ClosedRequestBoardColumns
          setActiveRequest={setActiveRequest}
          activeRequest={activeRequest}
          listRequestParams={tabs[LogisticTabs.closed].params}
          drawerIsOpen={drawerIsOpen}
          setDrawerIsOpen={setDrawerIsOpen}
          isFiltersLoaded={isFiltersLoaded}
        />
      </ContentWrapper>
    ),
  }

  const withoutTtn = {
    label: (
      <div>
        {t('tabs.without_ttn')}{' '}
        {'tripdocument' in eventsCount ? <Count color="orange">{eventsCount.tripdocument}</Count> : null}
      </div>
    ),
    key: LogisticTabs.without_ttn,
    children: (
      <TripsWithoutTtnTable
        listRequestParams={tabs[LogisticTabs.without_ttn].params}
        listParamsUpdated={tabs[LogisticTabs.without_ttn].setter}
        isFiltersLoaded={isFiltersLoaded}
      />
    ),
  }

  const carriersWithoutDocs = {
    label: tabs[LogisticTabs.carriersWithoutDocs].label,
    key: LogisticTabs.carriersWithoutDocs,
    children: (
      <CarriersWithoutDocsTable
        listRequestParams={tabs[LogisticTabs.carriersWithoutDocs].params}
        listParamsUpdated={tabs[LogisticTabs.carriersWithoutDocs].setter}
        isFiltersLoaded={isFiltersLoaded}
      />
    ),
  }

  const [prevActiveTab, setPrevActiveTab] = useState(activeTab)
  const memoizedDefaultFilter = useMemo(() => {
    return tabs[activeTab].initialParams?.filter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const changeRoute = useCallback(() => {
    if (!selectedRequest || prevActiveTab !== activeTab) {
      navigate(
        {
          pathname: generatePath(DealsKRRoutes.ListRequestsWithTab, { activeTab: activeTab }),
          search: location.search,
        },
        { replace: true },
      )
    } else {
      navigate(
        {
          pathname: generatePath(DealsKRRoutes.ListRequestsWithTabAndRequest, {
            activeTab: activeTab,
            selectedRequest,
          }),
          search: location.search,
        },
        { replace: true },
      )
    }

    setOptions({
      place: activeTab,
    })
  }, [activeTab, navigate, prevActiveTab, selectedRequest, setOptions])

  useEffect(() => {
    changeRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const changeActiveTab = useCallback(
    (tab) => {
      setPrevActiveTab(activeTab)
      setActiveTab(tab)
      setActiveRequest(undefined)
    },
    [activeTab],
  )

  return (
    <APageWrapper page={AnalyticsPages.REQUESTS} place={AnalyticsPlaces.REQUESTS_BOARD.PAGE}>
      <Layout.WrapperContent>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                { breadcrumbName: t('menu:newLogistics') },
                { path: DealsKRRoutes.List, breadcrumbName: t('logisticsKR:list:heading') },
              ]}
            />
            <Header.AbsoluteButtonWrapper>
              {activeRequest?.id && (
                <AButton
                  id="drawer"
                  color="secondary"
                  background="none"
                  Icon={Icons.IconHidemenu}
                  onClick={() => setDrawerIsOpen(true)}
                />
              )}
            </Header.AbsoluteButtonWrapper>
          </Layout.TopHeader>
          <Layout.PageName>{t('page_title')}</Layout.PageName>
          {activeTab === LogisticTabs.carriersWithoutDocs ? (
            <CarrierFilters
              defaultFilterValues={memoizedDefaultFilter}
              listRequestParams={tabs[activeTab].params}
              clearTabState={tabs[activeTab].clearTabState}
              updateFilterState={tabs[activeTab].updateFilterState}
            />
          ) : (
            <RequestsBoardFilters
              defaultFilterValues={memoizedDefaultFilter}
              listRequestParams={tabs[activeTab].params}
              clearTabState={tabs[activeTab].clearTabState}
              updateFilterState={tabs[activeTab].updateFilterState}
              activeTab={activeTab}
            />
          )}
        </Layout.Header>
        <AAntdTabs
          id="tabs"
          items={[openRequests, closedRequests, withoutTtn, carriersWithoutDocs]}
          activeKey={activeTab}
          onChange={changeActiveTab}
        />
      </Layout.WrapperContent>
    </APageWrapper>
  )
}
